/**
 * GameDateBanner Component
 * 
 * Displays the current date with navigation arrows and motivational tagline.
 * Uses magenta accent color for the date text.
 */

import React from 'react';
import { format, addDays, subDays, isToday } from 'date-fns';
import styles from './GameDateBanner.module.scss';

interface GameDateBannerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export const GameDateBanner: React.FC<GameDateBannerProps> = ({
  selectedDate,
  onDateChange,
}) => {
  const handlePrevDay = () => {
    onDateChange(subDays(selectedDate, 1));
  };

  const handleNextDay = () => {
    onDateChange(addDays(selectedDate, 1));
  };

  // Format: "Wed. Jan 7th"
  const formatDate = (date: Date): string => {
    const day = format(date, 'EEE');
    const month = format(date, 'MMM');
    const dayNum = format(date, 'd');
    const suffix = getDaySuffix(parseInt(dayNum));
    return `${day}. ${month} ${dayNum}${suffix}`;
  };

  const getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const todayLabel = isToday(selectedDate) ? 'Today' : '';

  return (
    <div className={styles.banner}>
      <div className={styles.dateRow}>
        <button
          className={styles.navButton}
          onClick={handlePrevDay}
          aria-label="Previous day"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className={styles.dateDisplay}>
          {todayLabel && <span className={styles.todayBadge}>{todayLabel}</span>}
          <span className={styles.date}>{formatDate(selectedDate)}</span>
        </div>
        
        <button
          className={styles.navButton}
          onClick={handleNextDay}
          aria-label="Next day"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      <p className={styles.tagline}>
        Plan your next <span className={styles.brand}>MiNU</span>tes. Let's go!
      </p>
    </div>
  );
};

export default GameDateBanner;
